import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useTimeOffRequests, useDecideTimeOffRequest } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { ClipboardCheck, Check, X } from 'lucide-react'
import { explainError } from '@/lib/explain-error'

export default function LeaveApprovalsPage() {
  const { toast } = useToast()
  const { data: pending } = useTimeOffRequests({ status: 'Pending', scope: 'all' })
  const decide = useDecideTimeOffRequest()

  const act = async (id: string, decision: 'approve' | 'reject') => {
    try {
      const res = await decide.mutateAsync({ id, decision })
      toast({ title: decision === 'approve' ? 'Approved' : 'Rejected' })
      if (res.warnings && res.warnings.length > 0) {
        toast({ title: 'Notification issue', description: res.warnings.join('; ') })
      }
    } catch (err: any) {
      toast({
        title: 'Action failed',
        description: explainError(err, 'Could not update the request.'),
        variant: 'destructive',
      })
    }
  }

  return (
    <main className="container mx-auto max-w-4xl p-6">
      <header className="mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ClipboardCheck className="h-6 w-6" aria-hidden="true" />
          Leave Approvals
        </h1>
        <p className="text-sm text-muted-foreground">
          {pending?.length ?? 0} request{(pending?.length ?? 0) === 1 ? '' : 's'} awaiting a
          decision
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Pending requests</CardTitle>
        </CardHeader>
        <CardContent>
          {!pending || pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing to approve right now.</p>
          ) : (
            <ul className="divide-y divide-border">
              {pending.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <p className="font-medium">
                      {r.first_name} {r.last_name}
                    </p>
                    <p className="text-muted-foreground">
                      {r.type_name} · {r.amount} {r.unit} · {r.start_date} → {r.end_date}
                      {r.reason ? ` · ${r.reason}` : ''}
                    </p>
                  </div>
                  <div className="flex flex-shrink-0 gap-2">
                    <Button
                      size="sm"
                      onClick={() => act(r.id, 'approve')}
                      disabled={decide.isPending}
                      aria-label={`Approve ${r.first_name} ${r.last_name}'s request`}
                    >
                      <Check className="mr-1 h-4 w-4" aria-hidden="true" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(r.id, 'reject')}
                      disabled={decide.isPending}
                      aria-label={`Reject ${r.first_name} ${r.last_name}'s request`}
                    >
                      <X className="mr-1 h-4 w-4" aria-hidden="true" />
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
